import React, { useState } from 'react';
import {Send, Copy, Save, ChevronDown, ChevronUp, Loader} from 'lucide-react';
import Anthropic from '@anthropic-ai/sdk';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './SalesAssistant.css';
import '../styles/markdown.css';
import {getGlobalData, getProjectData} from "../utils/projectStorage";

interface SalesResponse {
  id: string;
  question: string;
  dimension: string;
  response: string;
  timestamp: Date;
}

const SalesAssistant: React.FC = () => {
  const [question, setQuestion] = useState('');
  const [dimension, setDimension] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentResponse, setCurrentResponse] = useState('');
  const [responseHistory, setResponseHistory] = useState<SalesResponse[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  const dimensions = [
    'Achiever',
    'Driver',
    'Influencer',
    'Learner',
    'Optimizer',
    'Orchestrator',
    'Relator',
    'Visionary'
  ];

  const getGuidance = async () => {
    if (!question.trim() || !dimension) return;

    const globalData = getGlobalData();
    if (!globalData.apiKey) {
      alert('Please configure your Anthropic API key in Settings');
      return;
    }

    setLoading(true);
    setCurrentResponse('');

    try {
      const anthropic = new Anthropic({
        apiKey: globalData.apiKey,
        dangerouslyAllowBrowser: true
      });

      const ingestedContent = getProjectData('sales-assistant').ingestedContent || '';
      const salesPrompt = getProjectData('sales-assistant').salesPrompt || '';

      const prompt = `${salesPrompt}
      Customer Motivational Dimension: ${dimension}
      Sales Question/Challenge:
      ${question}`;

      // Get message history for context
      const messageHistory = getProjectData('sales-assistant').messageHistory || '[]';
      let previousMessages: any[] = [];
      
      if (messageHistory) {
        try {
          const history = JSON.parse(messageHistory);
          // Include relevant previous messages for context
          previousMessages = history.slice(-10).map((msg: any) => ({
            role: msg.role,
            content: msg.content
          }));
        } catch (error) {
          console.error('Error parsing message history:', error);
        }
      }

      // Get file IDs from project data
      const projectData = getProjectData('sales-assistant');
      const fileIds = projectData.fileIds || [];

      let files: any[] = [];
      // Build message content with file attachments
      const messageContent: any[] = [
        {
          type: 'text',
          text: prompt
        }
      ];

      // Add file references
      fileIds.forEach((fileId: string) => {
        files.push({
          type: 'document',
          source: {
            type: 'file',
            file_id: fileId
          }
        });
      });

      messageContent.push(...files);
      console.log(messageContent);

      const response = await anthropic.beta.messages.create({
        model: 'claude-sonnet-4-20250514',
        betas: ["files-api-2025-04-14"],
        max_tokens: 4096,
        system: [
          {
            type: 'text',
            text: ingestedContent,
            cache_control: { type: 'ephemeral' }
          }
        ],
        messages: [
          ...previousMessages,
          {
            role: 'user',
            content: messageContent
          }
        ]
      });

      const responseText = response.content[0].type === 'text' ? response.content[0].text : '';
      setCurrentResponse(responseText);

      // Add to history
      const newResponse: SalesResponse = {
        id: Date.now().toString(),
        question,
        dimension,
        response: responseText,
        timestamp: new Date()
      };
      setResponseHistory(prev => [newResponse, ...prev]);

      // Update shared message history
      const updatedHistory = [
        ...(previousMessages.length > 0 ? JSON.parse(messageHistory || '[]') : []),
        {
          id: Date.now().toString(),
          role: 'user',
          content: `[Sales Assistant - ${dimension}] ${question}`,
          timestamp: new Date()
        },
        {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: responseText,
          timestamp: new Date()
        }
      ];
      localStorage.setItem('messageHistory', JSON.stringify(updatedHistory));

    } catch (error) {
      console.error('Error getting guidance:', error);
      setCurrentResponse('Sorry, I encountered an error. Please check your API key and try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyResponse = () => {
    navigator.clipboard.writeText(currentResponse);
    // Could add a toast notification here
  };

  const saveResponse = () => {
    const savedResponses = JSON.parse(localStorage.getItem('savedResponses') || '[]');
    savedResponses.push({
      id: Date.now().toString(),
      question,
      dimension,
      response: currentResponse,
      timestamp: new Date()
    });
    localStorage.setItem('savedResponses', JSON.stringify(savedResponses));
    // Could add a toast notification here
  };

  return (
    <div className="sales-assistant-page">
      <div className="input-section">
        <div className="form-group">
          <label htmlFor="question">Sales Question</label>
          <textarea
            id="question"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Describe your sales challenge or customer question..."
            className="question-input"
            rows={4}
          />
        </div>

        <div className="form-group">
          <label htmlFor="dimension">Motivational Dimension</label>
          <select
            id="dimension"
            value={dimension}
            onChange={(e) => setDimension(e.target.value)}
            className="dimension-select"
          >
            <option value="">Select a dimension</option>
            {dimensions.map(dim => (
              <option key={dim} value={dim}>{dim}</option>
            ))}
          </select>
        </div>

        <button
          onClick={getGuidance}
          className="guidance-btn"
          disabled={loading || !question.trim() || !dimension}
        >
          {loading ? (
            <>
              <Loader className="spinner" size={16} />
              Loading...
            </>
          ) : (
            <>
              <Send size={20} /> Get Guidance
            </>
          )}
        </button>
      </div>

      {currentResponse && (
        <div className="response-section">
          <div className="response-header">
            <h3>AI Response</h3>
            <div className="response-actions">
              <button onClick={copyResponse} className="action-btn">
                <Copy size={16} /> Copy
              </button>
              <button onClick={saveResponse} className="action-btn">
                <Save size={16} /> Save
              </button>
            </div>
          </div>
          <div className="response-content markdown-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {currentResponse}
            </ReactMarkdown>
          </div>
        </div>
      )}

      {responseHistory.length > 0 && (
        <div className="history-section">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="history-toggle"
          >
            <span>Response History ({responseHistory.length})</span>
            {showHistory ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>

          {showHistory && (
            <div className="history-list">
              {responseHistory.map((item) => (
                <div key={item.id} className="history-item">
                  <div className="history-header">
                    <span className="history-dimension">{item.dimension}</span>
                    <span className="history-time">
                      {item.timestamp.toLocaleString()}
                    </span>
                  </div>
                  <div className="history-question">
                    {item.question}
                  </div>
                  <div className="history-response markdown-content">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {item.response}
                    </ReactMarkdown>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SalesAssistant;